import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Church,
  HeartPulse,
  Home,
  LogOut,
  Search,
  UserRound,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  globalSearch,
  type GlobalSearchResult,
} from "@/api/search";
import { useAuth } from "@/features/auth/hooks/useAuth";

export function Header() {
  const { churchId, user, logout } = useAuth();
  const navigate = useNavigate();
  const { permissions } = useEffectivePermissions();
  const canSearch = ["MEMBERS_VIEW", "HOUSEHOLDS_VIEW", "SERVICES_VIEW", "VISITORS_VIEW", "CARE_VIEW"].some(code => permissions.has(code));
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const normalizedSearch = search.trim();

  const displayName = useMemo(() => {
    if (!user) {
      return "Administrator";
    }

    const fullName = [
      user.first_name,
      user.last_name,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    return fullName || user.email || "Administrator";
  }, [user]);

  const displayTitle = useMemo(() => {
    const assignedTitle = user?.title?.trim();

    if (assignedTitle) {
      return assignedTitle;
    }

    switch (user?.role) {
      case "ADMIN":
        return "Administrator";

      case "STAFF":
        return "Staff";

      case "VOLUNTEER":
        return "Volunteer";

      default:
        return "Administrator";
    }
  }, [user]);

  const initials = useMemo(() => {
    if (!user) {
      return "AD";
    }

    const firstInitial =
      user.first_name?.trim().charAt(0) ?? "";

    const lastInitial =
      user.last_name?.trim().charAt(0) ?? "";

    const userInitials = (
      firstInitial + lastInitial
    ).toUpperCase();

    if (userInitials) {
      return userInitials;
    }

    return user.email
      ? user.email.charAt(0).toUpperCase()
      : "AD";
  }, [user]);

  const { data, isFetching, isError } = useQuery({
    queryKey: [
      "global-search",
      churchId,
      normalizedSearch,
    ],
    enabled:
      !!churchId && canSearch &&
      normalizedSearch.length >= 2,
    queryFn: () =>
      globalSearch(
        churchId!,
        normalizedSearch,
      ),
  });

  const groups = useMemo(() => {
    if (!data || !canSearch) {
      return [];
    }

    return [
      {
        key: "members",
        label: "Members",
        icon: Users,
        items: data.groups.members,
      },
      {
        key: "households",
        label: "Households",
        icon: Home,
        items: data.groups.households,
      },
      {
        key: "visitors",
        label: "Visitors",
        icon: UserRound,
        items: data.groups.visitors,
      },
      {
        key: "services",
        label: "Services",
        icon: Church,
        items: data.groups.services,
      },
      {
        key: "care_items",
        label: "Care/Follow-up",
        icon: HeartPulse,
        items: data.groups.care_items,
      },
    ].filter(
      (group) => group.items.length > 0,
    );
  }, [data, canSearch]);

  useEffect(() => {
    function handlePointerDown(
      event: MouseEvent,
    ) {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(
          event.target as Node,
        )
      ) {
        setOpen(false);
      }
    }

    window.addEventListener(
      "mousedown",
      handlePointerDown,
    );

    return () => {
      window.removeEventListener(
        "mousedown",
        handlePointerDown,
      );
    };
  }, []);

  function handleResultClick(
    result: GlobalSearchResult,
  ) {
    setSearch("");
    setOpen(false);
    navigate(result.path);
  }

  return (
    <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-slate-950/95 backdrop-blur">
      <div className="flex h-16 items-center justify-between px-8">
        <div
          ref={searchContainerRef}
          className={canSearch ? "relative hidden lg:block" : "hidden"}
        >
          <Search className="absolute left-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-500" />

          <input
            type="text"
            value={search}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              setSearch(event.target.value);
              setOpen(true);
            }}
            placeholder="Search MinistryIQ..."
            className="h-11 w-[28rem] rounded-xl border border-slate-700 bg-slate-900 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none xl:w-[32rem]"
          />

          {open &&
            normalizedSearch.length >= 2 && (
              <div className="absolute left-0 top-14 z-50 w-[34rem] overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl shadow-black/50">
                <div className="border-b border-slate-800 px-5 py-3">
                  <p className="text-xs font-medium text-slate-500">
                    Search results for
                  </p>

                  <p className="mt-1 text-sm font-semibold text-white">
                    &quot;{normalizedSearch}&quot;
                  </p>
                </div>

                <div className="max-h-[32rem] overflow-y-auto p-3">
                  {isFetching && (
                    <div className="px-4 py-10 text-center text-sm text-slate-500">
                      Searching MinistryIQ...
                    </div>
                  )}

                  {!isFetching && isError && (
                    <div className="px-4 py-10 text-center text-sm text-rose-400">
                      Unable to search MinistryIQ.
                    </div>
                  )}

                  {!isFetching &&
                    !isError &&
                    data &&
                    data.total === 0 && (
                      <div className="px-4 py-10 text-center">
                        <Search className="mx-auto h-8 w-8 text-slate-600" />

                        <p className="mt-3 text-sm font-medium text-white">
                          No results found
                        </p>

                        <p className="mt-1 text-xs text-slate-500">
                          Try a member, household,
                          visitor, service, or care
                          item.
                        </p>
                      </div>
                    )}

                  {!isFetching &&
                    !isError &&
                    groups.map((group) => {
                      const Icon = group.icon;

                      return (
                        <div
                          key={group.key}
                          className="mb-3 last:mb-0"
                        >
                          <div className="flex items-center gap-2 px-3 py-2">
                            <Icon className="h-4 w-4 text-indigo-400" />

                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                              {group.label}
                            </p>
                          </div>

                          <div className="space-y-1">
                            {group.items.map(
                              (result) => (
                                <button
                                  key={`${result.entity_type}-${result.id}`}
                                  type="button"
                                  onClick={() =>
                                    handleResultClick(
                                      result,
                                    )
                                  }
                                  className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-left transition hover:bg-indigo-500/10"
                                >
                                  <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-white">
                                      {result.title}
                                    </p>

                                    {result.subtitle && (
                                      <p className="mt-1 truncate text-xs text-slate-500">
                                        {
                                          result.subtitle
                                        }
                                      </p>
                                    )}
                                  </div>

                                  <span className="ml-4 text-xs font-medium text-indigo-400">
                                    Open -&gt;
                                  </span>
                                </button>
                              ),
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>

                {data && data.total > 0 && (
                  <div className="border-t border-slate-800 px-5 py-3 text-xs text-slate-500">
                    {data.total} result
                    {data.total === 1
                      ? ""
                      : "s"}{" "}
                    found
                  </div>
                )}
              </div>
            )}
        </div>

        <div className="ml-auto flex items-center gap-4">
          
          <button
            type="button"
            onClick={() => {
              logout();
              navigate("/login", { replace: true });
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-sm font-semibold text-slate-300 transition hover:border-slate-700 hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
          <div className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 px-3 py-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-sm font-bold text-white">
              {initials}
            </div>

            <div className="hidden min-w-0 xl:block">
              <p className="max-w-48 truncate text-sm font-semibold text-white">
                {displayName}
              </p>

              <p className="max-w-48 truncate text-xs text-slate-400">
                {displayTitle}
              </p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
